import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { FeatureSummary } from "@tdelivr/api-client/generated/configuration/feature-management.js";

import { boundedNote } from "../board.ts";
import { PLATFORM_FLAGS_SCOPE } from "../navigation.ts";
import {
  CREATE_REFUSAL,
  flagRow,
  flagScopeNote,
  groupByFlag,
  statesPresent,
  stateOf,
} from "../platform-flags.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `pflags` in `16 WP15 Platform Machinery.dc.html`: capabilities switched on per tenant.
 *
 * ```
 * pflags:['Platform flags','CFG-02','Capabilities switched on per tenant or platform-wide.',
 *   [['Flags','38','done',''],['Per tenant','14','active',''],
 *    ['Kill switches','4','lock','armed'],['Stale','2','caution','']],
 *   ['Flag','Scope','State'],
 *   [['commerce_module','ten_7Q2M, ten_3KD9','on for 2 of 5','active','partial'], …],
 *   'warn','Kill switches take effect in under 30 seconds and page the owning team.']
 * ```
 *
 * What is drawn from data and what is not:
 *
 * | drawn                                | source                                                 |
 * | ------------------------------------ | ------------------------------------------------------ |
 * | the rows                             | `CFG-02 GET /v1/platform/features` (ADR 0053)          |
 * | `Flag`                               | `features.flagKey`                                     |
 * | `Scope` — `ten_7Q2M, ten_3KD9`       | `features.tenantId`, grouped by key                    |
 * | `State` — `off (armed)`, `10%`       | `features.killSwitch`, `rolloutBasisPoints`, `defaultEnabled`, `targetingRules` |
 * | `on · expires 1 Nov`                 | **nothing** — CFG-02 holds no expiry. Q-151            |
 * | `platform` in the `Scope` column     | **nothing** — CFG-02 models no platform-wide flag. Q-190 |
 * | the four KPI tiles                   | **nothing** — R-04, and Q-178 carries the row          |
 * | `New platform flag`                  | **nothing** — both writes are `/internal/v1`. `CREATE_REFUSAL` |
 *
 * **This page needed only the tenancy change**, which is the answer to the question the brief asked
 * of each of the three. Every column it draws has a published source and had one before ADR 0053,
 * under `GET /v1/features` — the one thing missing was that that listing answers one tenant's rows
 * and this board's own column is `ten_7Q2M, ten_3KD9`. The absences it still carries are the ones
 * `tenant-admin · Feature flags` carries too, and were already recorded: the expiry (Q-151), the
 * stat tiles (R-04, Q-178) and the two-person write.
 *
 * `../platform-flags.ts` is where the grouping, the state reading and the fraction's denominator are
 * argued. Nothing about any of them is decided here.
 */

const features = services.configuration__feature_management;

export interface PlatformFlagsProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Platform › Settings › Platform flags`, which is the design's own trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Platform flags";
const SOURCE = "CFG-02";
const DESCRIPTION = "Capabilities switched on per tenant or platform-wide.";
const COLUMNS = ["Flag", "Scope", "State"] as const;
const NOTE = "Kill switches take effect in under 30 seconds and page the owning team.";
/** The design's own `noteK` for this page is `warn`, which its `NK` map draws in the amber tint. */
const NOTE_KIND = "warn" as const;
/** The design's `FORMS.pflags` primary button, word for word. */
const PRIMARY_LABEL = "New flag";

/**
 * The most rows one request answers, which is CFG-02's own maximum.
 *
 * Asked for explicitly rather than left to the service's default of fifty, because this board groups
 * by flag key across tenants: a page cut at fifty rows could split one flag's tenants across the
 * cut, and a group showing two of a flag's three tenants would draw `on for 2 of 2` under a heading
 * that means every tenant that holds it. `boundedNote` says when the answer was full, which is the
 * case where that can happen; **Q-194** records that the cursor is per row rather than per group,
 * so a second page cannot be assembled into whole groups either.
 */
const PER_FETCH = 200;

export function PlatformFlags({ transport, session, crumbs }: PlatformFlagsProps): ReactNode {
  const [rows, setRows] = useState<readonly FeatureSummary[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRows(undefined);
    setFailure(undefined);

    /*
     * Asked for before the call, not discovered from the refusal.
     *
     * The board renders the console's own forbidden state naming the scope, which is what an
     * operator can act on — they can ask their administrator for `feature-management:platform:read`
     * by name. A 403 from the service renders the same state, so this is not the only guard; it is
     * the one that makes the scope a literal in this application's source, which is what
     * `channel-scopes.mjs` derives the Keycloak grant from. ADR 0049 §3, and the notification bell's
     * three-console 403 is what happens without it.
     */
    if (!session.scopes.has(PLATFORM_FLAGS_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [PLATFORM_FLAGS_SCOPE] });
      return;
    }

    try {
      const client = features.createFeatureManagementClient(transport);
      /*
       * `listPlatformFeatureSummaries`, not `listTenantFeatureSummaries`.
       *
       * The two differ by one word and by every row they answer: the second is "scoped to the
       * authenticated tenant", which on this page would be a platform flag board showing one
       * tenant's flags under a heading that spans them all. ADR 0053 published them side by side and
       * left the first untouched.
       *
       * `after` is deliberately not sent. The first page is the board; see `PER_FETCH`.
       */
      const page = await client.listPlatformFeatureSummaries(String(PER_FETCH));
      setRows(page.features);
      setTruncated(page.nextAfter !== null);
    } catch (error) {
      setFailure(stateForError(error, [PLATFORM_FLAGS_SCOPE]));
    }
  }, [transport, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = rows === undefined ? undefined : groupByFlag(rows);
  const boardRows =
    groups === undefined
      ? undefined
      : groups.filter((group) => state === "" || stateOf(group).label === state).map(flagRow);

  const bounded = boundedNote(
    rows?.length,
    truncated,
    "flag versions",
    "ordered by tenant and then by flag key",
  );
  const span = flagScopeNote(rows);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No flag has been published on any tenant yet. A flag appears here when a platform engineer publishes its first version."
      primary={{ label: PRIMARY_LABEL, refusal: CREATE_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (groups === undefined ? [] : statesPresent(groups)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "warning", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(span === undefined ? {} : { footnote: span })}
      exportName="platform-flags"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
